import * as React from 'react';
import { GitFork, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Composer, readThinking, type ComposerSend } from './composer';
import {
  dedupePending,
  isDuplicateSubmit,
  recordSubmit,
  type SubmitRecord,
} from './submit-guard';
import { SingleChatView, type PendingMessage, type TranscriptMessage } from './single-chat-view';
import { useWs, type UseWs } from '@/hooks/use-ws';
import { useFreeRun } from '@/hooks/use-free-run';
import { parseFreeModelId } from '@/lib/free-models';
import { api } from '@/lib/api';
import { normalizeConfig } from '@/components/settings/settings';
import { resolveDefaultModel } from '@/components/providers/models';
import { useKnownSession, useProviderStore, useSessionStore } from '@/stores';
import { markSessionSeen } from '@/stores/session';
import { emitToast } from '@/components/shell';
import { FILE_DRAG_MIME, INSERT_MENTION_EVENT } from '@/components/files';
import { runMetaLabel } from './run-meta';

/**
 * Chat — one session: REST transcript + live WS stream + real Composer.
 * The socket is owned by AppShell (single socket per chat); this component
 * owns the transcript cache lifecycle, optimistic pending rows, slash
 * commands (/new /fork /model /rewind /help) and per-session model state.
 */

const MODEL_KEY = 'lokma-model';
/**
 * sessionStorage key prefix for a first prompt handed to a fresh session
 * (starter cards, `/new <prompt>`, REQ-057 agent `open_session`). Exported
 * so the shell can stage an agent prompt the same way — Chat consumes and
 * removes it on socket open, then auto-sends.
 */
export const INITIAL_PREFIX = 'lokma:initial:';

function readStoredModel(): string {
  try {
    return localStorage.getItem(MODEL_KEY) ?? '';
  } catch {
    return '';
  }
}

function isTranscriptMessage(m: unknown): m is TranscriptMessage {
  if (typeof m !== 'object' || m === null) return false;
  const r = (m as Record<string, unknown>).role;
  const c = (m as Record<string, unknown>).content;
  return (r === 'user' || r === 'assistant' || r === 'tool' || r === 'system') && typeof c === 'string';
}

export function Chat({
  sessionId,
  ws,
  onOpenSession,
  composerPlaceholder,
}: {
  sessionId: string;
  ws: UseWs;
  onOpenSession?: (id: string) => void;
  /**
   * REQ-161 — the Bots mode speaks in the bot's voice ("Message <bot>");
   * plain chat keeps the default composer copy.
   */
  composerPlaceholder?: string;
}) {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [model, setModel] = React.useState<string>('');
  const [pending, setPending] = React.useState<PendingMessage[]>([]);
  const [streamVisible, setStreamVisible] = React.useState(true);
  const [paletteSignal, setPaletteSignal] = React.useState(0);
  const [dropSignal, setDropSignal] = React.useState<{ path: string; key: number } | null>(null);
  const keySeq = React.useRef(0);
  /** REQ-156: last accepted submit — the duplicate-submit guard compares to it. */
  const lastSubmit = React.useRef<SubmitRecord>(null);
  const doneSeen = React.useRef(false);

  const transcripts = useSessionStore((s) => s.transcripts);
  const known = useKnownSession(sessionId);
  const loadTranscript = useSessionStore((s) => s.loadTranscript);
  const invalidateSession = useSessionStore((s) => s.invalidateSession);
  const storeModels = useProviderStore((s) => s.models);
  const providerLoading = useProviderStore((s) => s.loading);
  const refreshProviders = useProviderStore((s) => s.refresh);
  /** REQ-104: one smart-chain resolution per session (guard, not state — never re-fires). */
  const chainResolved = React.useRef<string | null>(null);

  const { status, stream, thinking, cost, reasoningPublished, done, lastError, lastErrorCode, retry, toolCalls, toolMarks, permissions, questions, sendText, interrupt, answerPermission, answerQuestion, clearLiveTrace, requestTranscript } = ws;
  const socketOpen = status === 'open';
  /**
   * REQ-149: the async paths below must know whether the socket is feeding
   * rows, without re-arming their effects on every status flip.
   */
  const socketOpenRef = React.useRef(false);
  socketOpenRef.current = socketOpen;
  // REQ-070: a backend run outlives refresh — the badge stays on while the
  // server reports running/queued even with no live stream on this socket.
  const [runActive, setRunActive] = React.useState(false);
  const streaming = (socketOpen && !done && stream.length > 0) || runActive;
  const [answerBusy, setAnswerBusy] = React.useState<string | null>(null);

  /**
   * REQ-205 slice 4: the client-free run (keyless OmniRoute pool). A picked
   * `free:` model routes the send here instead of the WS prompt path; the
   * same chat surface renders its deltas, and the settled exchange is
   * recorded back through the server's own transcript store.
   */
  const transcriptRef = React.useRef<TranscriptMessage[]>([]);
  const freeRun = useFreeRun(sessionId, ws, () => transcriptRef.current, {
    onSettled: () => {
      setPending([]);
      markSessionSeen(sessionId);
    },
  });
  const freePhase = freeRun.view.phase;
  /** A live or settled free run owns the live area — the WS buffers are idle. */
  const freeOwnsLive = freePhase !== 'idle';
  const liveStream = freeOwnsLive ? freeRun.view.text : streamVisible ? stream : '';
  const liveThinking = freeOwnsLive ? freeRun.view.reasoning : thinking;
  const liveStreaming = freePhase === 'running' || streaming;
  const liveRunError = freeOwnsLive ? freeRun.view.error : done && lastError ? lastError : null;
  const liveRunErrorCode = freeOwnsLive ? null : done && lastError ? lastErrorCode : null;

  // Loading line under the composer while the AI works (REQ-112): what is
  // happening right now — retry, approval wait, running tool, thinking,
  // writing, or generic working. Null when idle.
  const runStatus = React.useMemo((): string | null => {
    if (!liveStreaming) return null;
    if (retry) {
      const waitS = Math.round(retry.waitMs / 1000);
      return `Tekrar deneniyor (${retry.attempt}/${retry.maxAttempts}, ${waitS}sn)…`;
    }
    if (permissions.length > 0 || questions.length > 0) return 'Onay bekliyor…';
    const entries = Object.values(toolCalls);
    const running = [...entries].reverse().find((t) => t.result === undefined);
    if (running) return `Çalışıyor: ${running.tool}…`;
    if (liveThinking.trim().length > 0) return 'Düşünüyor…';
    if (liveStream.trim().length > 0) return 'Yazıyor…';
    return 'Çalışıyor…';
  }, [liveStreaming, retry, permissions.length, questions.length, toolCalls, liveThinking, liveStream]);

  // REQ-070: on mount (fresh boot after F5) ask the backend whether a run is
  // still in flight; while it is, poll status + transcript so the refresh
  // catches up live instead of showing a dead "complete".
  React.useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const poll = () => {
      api
        .getSessionRun(sessionId)
        .then((res) => {
          if (cancelled) return;
          const active = res.running || res.queued > 0;
          setRunActive((prev) => {
            // Idle edge after a live run: one final reload catches the tail
            // the last poll may have missed.
            if (prev && !active) void loadTranscript(sessionId, true);
            return active;
          });
          if (active) {
            // REQ-149: a live socket pushes every persisted row as
            // `transcript_append`, so this reload is only the socket-down
            // fallback. The probe itself stays — it also lights the
            // refresh-proof "working" badge (REQ-070).
            if (!socketOpenRef.current) void loadTranscript(sessionId, true);
            timer = setTimeout(poll, 4000);
          }
        })
        .catch(() => {
          if (!cancelled) setRunActive(false);
        });
    };
    void loadTranscript(sessionId).then(() => {
      if (!cancelled) poll();
    });
    return () => {
      cancelled = true;
      if (timer !== null) clearTimeout(timer);
    };
  }, [sessionId, loadTranscript]);

  // REQ-149: the pane subscribes to its session over the socket — the server
  // answers with a `transcript` snapshot and then PUSHES every persisted row as
  // `transcript_append`, so the conversation grows live. The hook remembers the
  // ask and repeats it after a reconnect, so one request catches the pane up.
  React.useEffect(() => {
    requestTranscript(sessionId);
  }, [sessionId, requestTranscript]);

  // Transcript + session model (server meta wins, tab storage is fallback).
  // While the server list is not in yet the effect re-runs when it flips —
  // waiting avoids doomed GETs for locally generated ids the server never
  // saw (fresh boots used to 404 twice per view).
  React.useEffect(() => {
    doneSeen.current = false;
    setPending([]);
    setStreamVisible(true);
    void refreshProviders();
    if (known === 'loading') {
      setModel(readStoredModel());
      return;
    }
    void loadTranscript(sessionId);
    if (!known) {
      setModel(readStoredModel());
      return;
    }
    api
      .getSession(sessionId)
      .then((detail) => {
        // REQ-117: a known session with no saved model resolves through the
        // global default chain (empty -> chain effect) — never the global
        // last-picked localStorage, which leaked one session's pick into
        // every fresh session.
        setModel(detail.model || '');
      })
      .catch(() => {
        setModel(readStoredModel());
      });
  }, [sessionId, loadTranscript, refreshProviders, known]);

  const transcript: TranscriptMessage[] = React.useMemo(() => {
    const raw = transcripts[sessionId] ?? [];
    return raw.filter(isTranscriptMessage);
  }, [transcripts, sessionId]);
  // REQ-205 slice 4: the free runner replays the recent conversation — the
  // getter reads through a ref so a mid-stream send sees the latest rows
  // without re-creating the hook callbacks on every transcript update.
  transcriptRef.current = transcript;

  /**
   * REQ-149: the socket pushes persisted rows live, so an optimistic pending
   * row is hidden the moment its server twin lands — otherwise the user's own
   * message painted twice (optimistic bubble + appended header row) until the
   * run finished. Only the TAIL is matched: the server appends the user row
   * right after the send, while an older identical prompt ("evet") must never
   * hide a fresh optimistic row.
   */
  const visiblePending = React.useMemo(() => {
    if (pending.length === 0) return pending;
    const tail = transcript.slice(-(pending.length + 4));
    const landed = new Set(tail.filter((m) => m.role === 'user').map((m) => m.content));
    // REQ-156: also collapse optimistic rows that carry the same text, so a
    // double submit can never paint the same prompt twice.
    const unique = dedupePending(pending);
    const next = unique.filter((p) => !landed.has(p.text));
    return next.length === unique.length && unique.length === pending.length ? pending : next;
  }, [pending, transcript]);

  // REQ-104: no session model and no stored model → smart default chain
  // (configured → most-used 30d → first enabled → built-in fallback).
  // Runs once per session after the catalog settles; an explicit user pick
  // (model !== '') always wins and is never overwritten.
  React.useEffect(() => {
    if (model !== '' || known === 'loading' || providerLoading) return;
    if (chainResolved.current === sessionId) return;
    chainResolved.current = sessionId;
    void (async () => {
      try {
        const [cfgRes, usageRes] = await Promise.all([
          api.getConfig().catch(() => null),
          api.getUsageSummary('30d').catch(() => null),
        ]);
        const resolved = resolveDefaultModel({
          configured: cfgRes ? normalizeConfig(cfgRes).defaultModel : '',
          usageTop: usageRes?.summary?.topModel ?? null,
          models: useProviderStore.getState().models,
        });
        // An explicit pick mid-flight stamps `:picked` — never overwrite it.
        if (chainResolved.current !== sessionId) return;
        if (!resolved.model) return;
        setModel(resolved.model);
        try {
          localStorage.setItem(MODEL_KEY, resolved.model);
        } catch {
          // Selection still applies for this tab without persistence.
        }
        if (known) {
          api.patchSession(sessionId, { model: resolved.model }).catch((e: Error) => {
            emitToast(`Model not saved server-side: ${e.message}`);
          });
        }
      } catch {
        // Keep empty — the server WS default applies to the run.
      }
    })();
  }, [model, known, sessionId, providerLoading]);

  const reloadTranscript = React.useCallback(async () => {
    invalidateSession(sessionId);
    // Forced: the finished stream created the session server-side even when
    // the list cache predates it — never take the unknown-id empty shortcut.
    await loadTranscript(sessionId, true);
  }, [invalidateSession, loadTranscript, sessionId]);

  // A finished stream means the server transcript grew — refetch, drop
  // optimistic rows, and hide the consumed live stream (no duplicates).
  React.useEffect(() => {
    if (!done || doneSeen.current) return;
    doneSeen.current = true;
    setPending([]);
    setRunActive(false);
    // REQ-121: watching it finish counts as read (sidebar dot clears).
    markSessionSeen(sessionId);
    // REQ-132: hide the live stream and drop the whole live trace (stream +
    // thinking + tool rows). The refetched transcript already carries that
    // answer — keeping both painted every reply twice, thinking block
    // included. Every finish path persists its output first (a clean reply, or
    // the partial text an aborted run wrote), so nothing is lost. `finally`
    // matters too: a failed refetch used to skip the hide and leave the
    // duplicate on screen.
    void reloadTranscript().finally(() => {
      setStreamVisible(false);
      clearLiveTrace();
    });
  }, [done, reloadTranscript, clearLiveTrace]);

  // REQ-038: upstream failures used to die silently (stuck "sending…").
  // The run now ends on `error` frames — surface the reason as a toast.
  const lastToastedError = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!lastError || lastToastedError.current === lastError) return;
    lastToastedError.current = lastError;
    // REQ-136: a paused run (turn budget spent) is not a failed send — the
    // wording has to match what the user is looking at.
    emitToast(lastErrorCode === 'turn_limit' ? `Run paused: ${lastError}` : `Send failed: ${lastError}`);
  }, [lastError, lastErrorCode]);

  // REQ-077: auto-retry is visible — one toast per attempt with the backoff
  // wait, so "hata alınca bekliyor" never looks like a hang.
  const lastToastedRetry = React.useRef<number>(0);
  React.useEffect(() => {
    if (!retry || lastToastedRetry.current >= retry.attempt) return;
    lastToastedRetry.current = retry.attempt;
    const waitS = Math.round(retry.waitMs / 1000);
    emitToast(`Tekrar deneniyor (${retry.attempt}/${retry.maxAttempts}, ${waitS}sn sonra)`);
  }, [retry]);

  // Starter cards / `/new <prompt>` hand a first prompt to the fresh session.
  React.useEffect(() => {
    if (status !== 'open') return;
    let initial: string | null = null;
    try {
      initial = sessionStorage.getItem(`${INITIAL_PREFIX}${sessionId}`);
      if (initial) sessionStorage.removeItem(`${INITIAL_PREFIX}${sessionId}`);
    } catch {
      initial = null;
    }
    if (initial) {
      // REQ-205: the handed-off first prompt rides the same router as a
      // composer send — a `free:` model goes through the keyless runner.
      send({ text: initial, model, contextPaths: [], reasoningEffort: readThinking(), images: [], files: [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, sessionId]);

  // Keep the tail visible while tokens stream in.
  React.useEffect(() => {
    const el = scrollRef.current;
    if (el && stream) el.scrollTop = el.scrollHeight;
  }, [stream, transcript.length]);

  const pickModel = React.useCallback(
    (id: string) => {
      setModel(id);
      chainResolved.current = `${sessionId}:picked`;
      try {
        localStorage.setItem(MODEL_KEY, id);
      } catch {
        // Selection still applies for this tab without persistence.
      }
      api.patchSession(sessionId, { model: id }).catch((e: Error) => {
        emitToast(`Model not saved server-side: ${e.message}`);
      });
    },
    [sessionId],
  );

  const send = React.useCallback(
    (s: ComposerSend) => {
      const freeId = parseFreeModelId(s.model);
      // REQ-205 slice 4: a `free:` pick never reaches the server prompt path —
      // the browser talks to the keyless pool directly. Second gate for
      // programmatic callers (the composer refuses earlier, with the draft
      // intact); the free wire is text-only in this version.
      if (freeId && (s.images.length > 0 || s.files.length > 0)) {
        emitToast('Free models are text-only here — remove the attachments or pick a provider model.');
        return;
      }
      if (freeId && freeRun.view.phase === 'running') {
        emitToast('A free-model run is already in flight — wait for it or press Stop.');
        return;
      }
      // REQ-156: a fast second Enter (or Enter + click) leaves the composer text
      // in place and used to send the same prompt twice — the user's own message
      // then painted twice optimistically plus the server echo (three copies of
      // one transcript row). Drop the accidental repeat; a later re-ask of the
      // same text is still allowed.
      const now = Date.now();
      if (isDuplicateSubmit(lastSubmit.current, s.text, now)) return;
      lastSubmit.current = recordSubmit(s.text, now);
      keySeq.current += 1;
      // REQ-187: a file-only send has no text — the optimistic row names the
      // files instead of painting an empty bubble.
      const pendingText = s.text || s.files.map((f) => f.name).join(', ');
      setPending((prev) => [...prev, { key: keySeq.current, text: pendingText }]);
      setStreamVisible(true);
      doneSeen.current = false;
      if (freeId) {
        // A new prompt starts a new free view; the previous run's settled
        // notice belongs to it, not to this one.
        freeRun.reset();
        if (!freeRun.start(s.text, freeId)) {
          // Refused (an empty prompt reached this path programmatically): the
          // optimistic row would never get a run — drop it and say why.
          setPending((prev) => prev.filter((p) => p.key !== keySeq.current));
          emitToast('Nothing to send to the free model — type a prompt first.');
        }
        return;
      }
      sendText(s.text, {
        model: s.model || undefined,
        contextPaths: s.contextPaths.length ? s.contextPaths : undefined,
        reasoningEffort: s.reasoningEffort === 'off' ? undefined : s.reasoningEffort,
        images: s.images.length ? s.images : undefined,
        files: s.files.length ? s.files : undefined,
      });
    },
    [freeRun, sendText],
  );

  /**
   * Stop is ONE affordance for both paths: a live free run kills its fetch
   * (partial text survives, then persists), anything else interrupts the
   * server-side run over WS.
   */
  const stop = React.useCallback(() => {
    if (freeRun.view.phase === 'running') {
      freeRun.abort();
      return;
    }
    interrupt();
  }, [freeRun, interrupt]);

  const openSession = React.useCallback(
    (id: string, initialPrompt?: string) => {
      if (initialPrompt) {
        try {
          sessionStorage.setItem(`${INITIAL_PREFIX}${id}`, initialPrompt);
        } catch {
          // Fresh session still opens; the prompt is dropped only for this tab.
        }
      }
      if (onOpenSession) {
        onOpenSession(id);
      } else {
        emitToast(`Session ${id} ready`);
      }
    },
    [onOpenSession],
  );

  const runSlash = React.useCallback(
    (id: string, args: string) => {
      switch (id) {
        case 'new': {
          api
            .createSession(model ? { model } : {})
            .then((res) => openSession(res.id, args || undefined))
            .catch((e: Error) => emitToast(`New session failed: ${e.message}`));
          break;
        }
        case 'fork': {
          api
            .forkSession(sessionId)
            .then((res) => {
              emitToast(`Forked ${res.copied ?? 0} messages`);
              openSession(res.id);
            })
            .catch((e: Error) => emitToast(`Fork failed: ${e.message}`));
          break;
        }
        case 'model': {
          if (!args) {
            emitToast(`Current model: ${model || '(server default)'}`);
            break;
          }
          const hit =
            storeModels.find((m) => m.id === args) ??
            storeModels.find((m) => m.id.endsWith(`/${args}`)) ??
            storeModels.find((m) => m.label.toLowerCase() === args.toLowerCase());
          if (!hit) {
            emitToast(`Unknown model ${args} — pick from the Composer list`);
            break;
          }
          pickModel(hit.id);
          emitToast(`Model: ${hit.label}`);
          break;
        }
        case 'rewind': {
          const keep = Number.parseInt(args, 10);
          if (!Number.isFinite(keep) || keep < 0) {
            emitToast('Usage: /rewind <message count>');
            break;
          }
          api
            .rewindSession(sessionId, keep)
            .then((res) => {
              emitToast(`Rewound to ${res.kept} messages`);
              void reloadTranscript();
            })
            .catch((e: Error) => emitToast(`Rewind failed: ${e.message}`));
          break;
        }
        case 'help': {
          setPaletteSignal((n) => n + 1);
          break;
        }
        default: {
          emitToast(`Unknown command /${id} — try /help`);
        }
      }
    },
    [model, openSession, pickModel, reloadTranscript, sessionId, storeModels],
  );

  // Permission answers travel over WS; `always` additionally persists the tool
  // as an allow-rule via PATCH /api/config (same store the Settings pane edits).
  // The answer is always sent — a failed persist only toasts, never blocks.
  const handleAnswerPermission = React.useCallback(
    (requestId: string, decision: 'allow' | 'deny' | 'always') => {
      if (decision !== 'always') {
        answerPermission(requestId, decision);
        return;
      }
      const pending = permissions.find((p) => p.requestId === requestId);
      setAnswerBusy(requestId);
      const persist = pending
        ? api
            .getConfig()
            .then((res) => {
              const perms = (res.config as { permissions?: { allow?: string[]; deny?: string[]; defaultMode?: string } })
                .permissions ?? { allow: [] as string[], deny: [] as string[], defaultMode: 'auto' as const };
              const allow = perms.allow ?? [];
              const rule = pending.tool;
              const next = allow.includes(rule) ? allow : [...allow, rule];
              return api.patchConfig({
                permissions: { allow: next, deny: perms.deny ?? [], defaultMode: perms.defaultMode ?? 'auto' },
              });
            })
            .then(() => emitToast(`Always allowing ${pending.tool}`))
            .catch((e: Error) => emitToast(`Rule not saved: ${e.message}`))
        : Promise.resolve();
      void persist.finally(() => {
        setAnswerBusy(null);
        answerPermission(requestId, decision);
      });
    },
    [answerPermission, permissions],
  );

  const handleAnswerQuestion = React.useCallback(
    (requestId: string, answer: string) => {
      answerQuestion(requestId, answer);
    },
    [answerQuestion],
  );

  const copyText = React.useCallback((text: string) => {
    try {
      void navigator.clipboard.writeText(text).then(
        () => emitToast('Copied'),
        () => emitToast('Copy failed'),
      );
    } catch {
      emitToast('Copy failed');
    }
  }, []);

  const editSave = React.useCallback(
    (index: number, text: string) => {
      // Save & rewind: truncate everything from the edited message on, then
      // resend — the server transcript is the checkpoint, not the UI scroll.
      api
        .rewindSession(sessionId, index)
        .then(() => reloadTranscript())
        .then(() => {
          // REQ-205: the resend rides the same router as a normal send — a
          // `free:` model must not fall back onto the server prompt path.
          send({ text, model, contextPaths: [], reasoningEffort: readThinking(), images: [], files: [] });
        })
        .catch((e: Error) => emitToast(`Edit failed: ${e.message}`));
    },
    [sessionId, reloadTranscript, send, model],
  );

  const rewindTo = React.useCallback(
    (index: number) => {
      api
        .rewindSession(sessionId, index)
        .then((res) => {
          emitToast(`Rewound to ${res.kept} messages`);
          void reloadTranscript();
        })
        .catch((e: Error) => emitToast(`Rewind failed: ${e.message}`));
    },
    [sessionId, reloadTranscript],
  );

  const forkHere = React.useCallback(() => {
    api
      .forkSession(sessionId)
      .then((res) => {
        emitToast(`Forked ${res.copied ?? 0} messages`);
        openSession(res.id);
      })
      .catch((e: Error) => emitToast(`Fork failed: ${e.message}`));
  }, [sessionId, openSession]);

  const startStarter = React.useCallback(
    (prompt: string) => {
      if (transcript.length === 0 && pending.length === 0 && !stream) {
        // Starter cards honour the persisted thinking pick (REQ-133).
        send({ text: prompt, model, contextPaths: [], reasoningEffort: readThinking(), images: [], files: [] });
        return;
      }
      api
        .createSession(model ? { model } : {})
        .then((res) => openSession(res.id, prompt))
        .catch((e: Error) => emitToast(`New session failed: ${e.message}`));
    },
    [transcript.length, pending.length, stream, send, model, openSession],
  );

  // Explorer drops + context-menu "Insert @mention" land here as a signal
  // the Composer splices into its draft (existing mention → contextPaths).
  const insertMention = React.useCallback((path: string) => {
    const clean = path.trim().replace(/^@/, '');
    if (!clean) return;
    keySeq.current += 1;
    setDropSignal({ path: clean, key: keySeq.current });
  }, []);

  React.useEffect(() => {
    const onMention = (e: Event): void => {
      insertMention((e as CustomEvent<string>).detail ?? '');
    };
    window.addEventListener(INSERT_MENTION_EVENT, onMention);
    return () => window.removeEventListener(INSERT_MENTION_EVENT, onMention);
  }, [insertMention]);

  const onChatDrop = React.useCallback(
    (e: React.DragEvent) => {
      const direct = e.dataTransfer.getData(FILE_DRAG_MIME);
      const plain = e.dataTransfer.getData('text/plain');
      const path = (direct || (plain.startsWith('@') ? plain : '')).trim().replace(/^@/, '');
      if (!path) return;
      e.preventDefault();
      insertMention(path);
      emitToast(`Attached @${path}`);
    },
    [insertMention],
  );

  // REQ-174: cost badge + model + the thinking level the run applied (and an
  // honest callout when the run asked for reasoning but published none).
  const costLabel = runMetaLabel(cost, reasoningPublished);

  return (
    <Card
      className="flex flex-1 flex-col overflow-hidden"
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes(FILE_DRAG_MIME) || e.dataTransfer.types.includes('text/plain')) {
          e.preventDefault();
        }
      }}
      onDrop={onChatDrop}
    >
      <div className="flex h-9 items-center gap-2 border-b px-3 text-xs text-muted-foreground">
        <span className="font-mono">{sessionId}</span>
        <span className="ml-auto flex items-center gap-1">
          <Button variant="ghost" size="sm" className="h-6 px-2 text-[11px]" title="New session" aria-label="New session" onClick={() => runSlash('new', '')}>
            <Plus className="h-3 w-3" />
          </Button>
          <Button variant="ghost" size="sm" className="h-6 px-2 text-[11px]" title="Fork session" aria-label="Fork session" onClick={forkHere}>
            <GitFork className="h-3 w-3" />
          </Button>
          <span className="capitalize">{status}</span>
        </span>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-auto p-3">
        <SingleChatView
          scrollRef={scrollRef}
          cwd={known !== null && typeof known === 'object' ? known.cwd : undefined}
          transcript={transcript}
          pending={visiblePending}
          stream={liveStream}
          streaming={liveStreaming}
          thinking={liveThinking}
          runError={liveRunError}
          runErrorCode={liveRunErrorCode}
          costLabel={costLabel}
          toolCalls={toolCalls}
          toolMarks={toolMarks}
          permissions={permissions}
          questions={questions}
          answerBusy={answerBusy}
          onEditSave={editSave}
          onRewindTo={rewindTo}
          onCopy={copyText}
          onFork={forkHere}
          onStart={startStarter}
          onAnswerPermission={handleAnswerPermission}
          onAnswerQuestion={handleAnswerQuestion}
        />
      </div>
      <div className="p-3 pt-0">
        <span role="status" aria-live="polite" className="sr-only">
          {streaming ? 'Lokma is streaming a response.' : 'Response complete.'}
        </span>
        <Composer
          model={model}
          streaming={liveStreaming}
          status={runStatus}
          socketOpen={socketOpen}
          paletteSignal={paletteSignal}
          dropSignal={dropSignal}
          onSend={send}
          onStop={stop}
          onSlash={runSlash}
          onPickModel={pickModel}
          placeholder={composerPlaceholder}
        />
      </div>
    </Card>
  );
}

/** Standalone Chat — owns its socket (used outside AppShell). */
export function ChatWithSocket({ sessionId }: { sessionId: string }) {
  const ws = useWs(sessionId);
  return (
    <Chat
      sessionId={sessionId}
      ws={ws}
      onOpenSession={(id) => {
        try {
          localStorage.setItem('lokma:sessionId', id);
        } catch {
          // Reload still switches only when storage works.
        }
        window.location.reload();
      }}
    />
  );
}
